import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  CanDeactivatePage,
  OperationCancelledError,
  SkyKickModal,
  SkyKickModalService,
  SkyKickModalWarningOptions,
  TaskManagerService,
  WarningModalComponent,
} from '@skykick/core';
import { AbstractUserProvider } from '@skykick/platform-identity-auth-auth0-angular';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  forkJoin,
  fromEvent,
  map,
  merge,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';
import { Contact } from '../core/models/Contact';
import { Products, ProductType } from '../core/models/ProductType';
import { NotificationsAdminCenterResourceService } from '../core/services/notifications-admin-center-resource.service';
import { getDefaultODataEventSearchString } from '../core/utils/event-search-filters.util';
import { isEqual, groupBy, cloneDeep } from 'lodash-es';
import { ODataNotificationAdminCenterEvent } from '../core/models/ODataNotificationAdminCenterEvent';
import { DeliveryMediums } from '../core/utils/delivery-medium.util';
import {
  EventSeverityToIconClass,
  EventSeverityToIconName,
  EventSeverityValues,
} from '../core/utils/event-severity.util';
import { NotificationAdminCenterEventSettingsRequest } from '../core/models/NotificationAdminCenterEventSettingsRequest';
import { NotificationDetailsPanelComponent } from '../core/notifications-details-panel/notification-details-panel.component';
import { mapToEventPreferencesRequest } from '../core/utils/event-preferences.util';
import { CategoryContactAdminCenter } from '../core/models/CategoryContactAdminCenter';
import { DeliveryPreferenceAdminCenter } from '../core/models/DeliveryPreferenceAdminCenter';
import { formatValue } from '../core/utils/format.util';
import { TaskType } from '../../models/task-type';
import NotificationDetailsViewModel from '../core/notifications-details-panel/models/notification-details-view-model';
import { FilterArea } from '../../models/filter-area';
import { FilterItem } from '../../models/filter-item';
import { EventVisibleModel } from '../core/models/EventVisibleModel';

@Component({
  selector: 'sk-product-notifications-admin-center',
  templateUrl: './product-notifications-admin-center.component.html',
  styleUrl: './product-notifications-admin-center.component.scss',
})
export class ProductNotificationsAdminCenterComponent
  extends CanDeactivatePage
  implements OnInit, OnDestroy, AfterViewInit
{
  constructor(
    private readonly notificationsAdminCenterResourceService: NotificationsAdminCenterResourceService,
    private readonly toastrService: ToastrService,
    private readonly userService: AbstractUserProvider,
    private readonly taskManagerService: TaskManagerService,
    private readonly route: ActivatedRoute,
    translateService: TranslateService,
    modalService: SkyKickModalService
  ) {
    super(modalService, translateService);
  }

  @ViewChild('searchEventsInput')
  searchEventsInput: ElementRef;
  @ViewChild('clearSearchBtn')
  clearSearchBtn: ElementRef;
  @ViewChild('searchEventsBtn')
  searchEventsBtn: ElementRef;

  get searchInputValue(): string {
    return this.searchEventsInput.nativeElement.value;
  }

  set searchInputValue(value: string) {
    this.searchEventsInput.nativeElement.value = value;
  }

  filterArea: FilterArea;

  eventSeverityToIconClass = EventSeverityToIconClass;
  eventSeverityToIconName = EventSeverityToIconName;
  loadingProductPreferences = false;
  productPreferencesFailedToLoad = false;
  initialProductPreferences: Contact[];
  newProductPreferences: Contact[];
  initialCategoryPreferences: CategoryContactAdminCenter[];
  newCategoryPreferences: CategoryContactAdminCenter[];
  initialGroupedEvents: [string, ODataNotificationAdminCenterEvent[]][];
  newGroupedEvents: [string, ODataNotificationAdminCenterEvent[]][];
  productKey: string;
  productName: string;
  searchTermSubject$ = new BehaviorSubject<string>('');

  groupedEventsVisibleFlags: [string, EventVisibleModel[]][];
  accordionItemsHide: [string, BehaviorSubject<boolean>][];

  savingPreferences = false;
  resetPreferencesOnCancel = new Subject<void>();
  updateInitialPreferencesOnSave = new Subject<void>();

  canBeCollapsed: boolean = false;
  expandedSections: { [key: string]: boolean };

  private readonly onDestroy$ = new Subject<void>();

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.productName = params.get('product');
      this.initData();
      this.setSeverityFilter();

      const product = Products.find((p) => p.name === this.productName);
      this.productKey = product.id;
      this.loadingProductPreferences = true;
      const defaultSearchString = getDefaultODataEventSearchString(
        product.notificationsName
      );

      forkJoin([
        this.notificationsAdminCenterResourceService.getProductLevelPreferences(
          this.productKey
        ),
        this.notificationsAdminCenterResourceService.getODataEvents(
          defaultSearchString,
          this.productKey
        ),
      ])
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: ([productLevelPreferences, eventsResponse]) => {
            this.loadingProductPreferences = false;
            this.initialProductPreferences = cloneDeep(
              productLevelPreferences.contacts
            );
            this.newProductPreferences = cloneDeep(
              productLevelPreferences.contacts
            );
            this.initialCategoryPreferences = cloneDeep(
              productLevelPreferences.categoryContacts
            );
            this.newCategoryPreferences = cloneDeep(
              productLevelPreferences.categoryContacts
            );
            const groupedEvents = Object.entries(
              groupBy(
                eventsResponse.value,
                (e) => `${e.CategoryKey.toLowerCase()}_${e.CategoryName}`
              )
            );

            this.initialGroupedEvents = cloneDeep(groupedEvents);
            this.newGroupedEvents = cloneDeep(groupedEvents);

            this.newGroupedEvents.forEach(([key, value]) => {
              this.expandedSections[key] = true;
              this.groupedEventsVisibleFlags.push([
                key,
                value.map((v) => {
                  return {
                    Key: v.Key,
                    Visible: true,
                  };
                }),
              ]);
              this.accordionItemsHide.push([key, new BehaviorSubject(false)]);
            });
          },
          error: () => {
            this.loadingProductPreferences = false;
            this.productPreferencesFailedToLoad = true;
          },
        });
    });

    this.searchTermSubject$.pipe(debounceTime(500)).subscribe((value) => {
      this.filterGroupedEvents(this.getSearchTermValue(value));
    });
  }

  ngAfterViewInit(): void {
    this.searchInputObservable().subscribe((value) => {
      this.searchTermSubject$.next(value);
    });
  }

  initData() {
    if (this.searchEventsInput) {
      this.searchEventsInput.nativeElement.value = '';
    }
    this.groupedEventsVisibleFlags = [];
    this.accordionItemsHide = [];
    this.expandedSections = {};
    this.initialGroupedEvents = [];
    this.newGroupedEvents = [];
    this.initialProductPreferences = [];
    this.newProductPreferences = [];
    this.initialCategoryPreferences = [];
    this.newCategoryPreferences = [];
  }

  toggleAllSections(): void {
    this.canBeCollapsed = !this.canBeCollapsed;
    this.newGroupedEvents.forEach(([key]) => {
      this.expandedSections[key] = this.canBeCollapsed;
    });
  }

  onSectionToggle(sectionKey: string, isExpanded: boolean): void {
    this.expandedSections[sectionKey] = isExpanded;

    this.canBeCollapsed = Object.values(this.expandedSections).some(
      (expanded) => expanded
    );
  }

  updateProductPreferences(preferences: Contact[]) {
    this.newProductPreferences = cloneDeep(preferences);
  }

  updateCategoryPreferences(preferences: CategoryContactAdminCenter[]) {
    this.newCategoryPreferences = cloneDeep(preferences);
  }

  updateCategoryPreferencesContacts(
    categoryKey: string,
    preferencesContacts: Contact[]
  ) {
    const categoryPreference = this.newCategoryPreferences.find(
      (p) => p.categoryKey === categoryKey
    );

    if (categoryPreference) {
      categoryPreference.contacts = preferencesContacts;
    } else {
      this.newCategoryPreferences.push({
        categoryKey: categoryKey,
        contacts: preferencesContacts,
      });
    }
  }

  updateEventPreferences(
    groupedEvents: [string, ODataNotificationAdminCenterEvent[]][]
  ) {
    this.newGroupedEvents = cloneDeep(groupedEvents);
  }

  getCategoryPreferences(categoryKey: string) {
    return (
      this.newCategoryPreferences.find((c) => c.categoryKey === categoryKey)
        ?.contacts ?? []
    );
  }

  save() {
    this.savingPreferences = true;
    const user = this.userService.getCurrentUser();

    const productPreferences: DeliveryPreferenceAdminCenter = {
      productKey: this.productKey,
      createdBy: user.fullName,
      contacts: this.newProductPreferences,
      categoryContacts: this.newCategoryPreferences,
    };

    const eventPreferences: NotificationAdminCenterEventSettingsRequest = {
      partnerId: user.partnerId,
      createdBy: user.fullName,
      notificationAdminCenterEventConfigurationSettings:
        mapToEventPreferencesRequest(this.newGroupedEvents),
    };

    forkJoin([
      this.notificationsAdminCenterResourceService.syncProductLevelPreferences(
        user.partnerId,
        productPreferences
      ),
      this.notificationsAdminCenterResourceService.syncEventPreferences(
        eventPreferences,
        this.productKey
      ),
    ])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: () => {
          this.initialProductPreferences = cloneDeep(
            this.newProductPreferences
          );
          this.initialCategoryPreferences = cloneDeep(
            this.newCategoryPreferences
          );
          this.initialGroupedEvents = cloneDeep(this.newGroupedEvents);
          this.updateInitialPreferencesOnSave.next();
          this.toastrService.success(
            this.translateService.instant(
              `sk-notifications-admin-center.preferences.${this.productName}.save_success_message`
            )
          );
        },
        error: () => {
          this.toastrService.error(
            this.translateService.instant(
              `sk-notifications-admin-center.preferences.${this.productName}.save_error_message`
            )
          );
        },
      })
      .add(() => {
        this.savingPreferences = false;
      });
  }

  cancel() {
    if (this.preferencesChanged()) {
      let warningModal: SkyKickModal<WarningModalComponent, void>;
      const options: SkyKickModalWarningOptions = {
        body: this.translateService.instant(
          `sk-notifications-admin-center.preferences.${this.productName}.cancel_modal_body`
        ),
        title: this.translateService.instant(
          `sk-notifications-admin-center.preferences.${this.productName}.cancel_modal_title`
        ),
        btnLabel: this.translateService.instant(
          `sk-notifications-admin-center.preferences.general.cancel_modal_ok_button_text`
        ),
        alternative: {
          btnLabel: this.translateService.instant(
            `sk-notifications-admin-center.preferences.general.cancel_modal_cancel_button_text`
          ),
          btnCallback: () => {
            warningModal.dismiss();
          },
        },
      };
      warningModal = this.modalService.warning(options);
      warningModal.result.then((res) => {
        if (res.wasClosed) {
          this.updateProductPreferences(this.initialProductPreferences);
          this.updateCategoryPreferences(this.initialCategoryPreferences);
          this.updateEventPreferences(this.initialGroupedEvents);
          this.resetPreferencesOnCancel.next();
        }
      });
    }
  }

  getMediumDisplayName(deliveryMedium: string) {
    return DeliveryMediums.find((d) => d.name === deliveryMedium).displayName;
  }

  getConfigurations(groupKey: string, deliveryMedium: string) {
    return this.newGroupedEvents
      .find(([key, _]) => key === groupKey)[1]
      .filter((e) =>
        this.groupedEventsVisibleFlags.some(
          ([key, value]) =>
            key === groupKey && value.some((v) => v.Key === e.Key && v.Visible)
        )
      )
      .flatMap((e) =>
        e.NotificationAdminCenterEventConfigurations.filter(
          (c) => c.DistributionMedium === deliveryMedium
        )
      );
  }

  getGeneralNotConfigurableByAdminValueForMedium(
    groupKey: string,
    deliveryMedium: string
  ) {
    return this.getConfigurations(groupKey, deliveryMedium).every(
      (c) => !c.ConfigurableByAdmin
    );
  }

  getGeneralCheckedValueForMedium(groupKey: string, deliveryMedium: string) {
    const configurations = this.getConfigurations(groupKey, deliveryMedium);

    // if all configurations are not configurable by admin and not checked return false
    // else if all configurations are checked or not configurable by admin return true
    // else return false
    return (
      !configurations.every((c) => !c.ConfigurableByAdmin && !c.Checked) &&
      configurations.every((c) => c.Checked || !c.ConfigurableByAdmin)
    );
  }

  checkGeneralCheckbox(
    groupKey: string,
    deliveryMedium: string,
    value: boolean
  ) {
    this.getConfigurations(groupKey, deliveryMedium).forEach(
      (v) => (v.Checked = v.ConfigurableByAdmin ? value : v.Checked)
    );
  }

  checkSingleCheckbox(
    groupKey: string,
    eventConfigurationKey: string,
    value: boolean
  ) {
    const configuration = this.newGroupedEvents
      .find(([key, _]) => key === groupKey)[1]
      .flatMap((e) => e.NotificationAdminCenterEventConfigurations)
      .find((c) => c.EventConfigurationKey === eventConfigurationKey);

    configuration.Checked = configuration.ConfigurableByAdmin
      ? value
      : configuration.Checked;
  }

  getEventGroupName(eventGroup: [string, ODataNotificationAdminCenterEvent[]]) {
    return eventGroup[0];
  }

  getEventGroupValue(
    eventGroup: [string, ODataNotificationAdminCenterEvent[]]
  ) {
    return eventGroup[1];
  }

  getCategoryKeyFromEventGroupName(
    eventGroup: [string, ODataNotificationAdminCenterEvent[]]
  ) {
    return this.getEventGroupName(eventGroup).split('_')[0];
  }

  getCategoryNameFromEventGroupName(
    eventGroup: [string, ODataNotificationAdminCenterEvent[]]
  ) {
    return this.getEventGroupName(eventGroup).split('_')[1];
  }

  resetDefaultCheckboxesState(): void {
    this.newGroupedEvents.forEach(([_, events]) => {
      events.forEach((event) => {
        event.NotificationAdminCenterEventConfigurations.forEach((config) => {
          if (config.ConfigurableByAdmin) {
            config.Checked = true;
          }
        });
      });
    });
  }

  preferencesChanged() {
    this.showCanDeactivateModal =
      !isEqual(this.initialProductPreferences, this.newProductPreferences) ||
      !isEqual(
        this.prepareCategoryPreferencesForEqualityCheck(
          this.initialCategoryPreferences
        ),
        this.prepareCategoryPreferencesForEqualityCheck(
          this.newCategoryPreferences
        )
      ) ||
      !isEqual(this.initialGroupedEvents, this.newGroupedEvents);
    return this.showCanDeactivateModal;
  }

  prepareCategoryPreferencesForEqualityCheck(
    categoryPreferences: CategoryContactAdminCenter[]
  ) {
    return categoryPreferences.filter((c) => c.contacts.length > 0);
  }

  selectNotification(
    notification: ODataNotificationAdminCenterEvent,
    event: Event
  ): void {
    const target = event.target as HTMLElement;
    if (target.closest('.notification-checkbox-cell')) return;

    const viewModel: NotificationDetailsViewModel = {
      name: notification.Name,
      severity: notification.EventSeverity,
      category: notification.CategoryName,
      subject: formatValue(notification?.Subject),
      body: formatValue(notification?.Body),
    };

    this.taskManagerService.configureTask(
      TaskType.NotificationsDetails,
      viewModel,
      NotificationDetailsPanelComponent
    );
    this.taskManagerService
      .activateTask(TaskType.NotificationsDetails)
      .catch((e) => {
        OperationCancelledError.suppressOnCancel(e);
      });
  }

  isButtonDisabled() {
    return (
      this.loadingProductPreferences ||
      this.productPreferencesFailedToLoad ||
      this.savingPreferences ||
      !this.preferencesChanged()
    );
  }

  filterGroupedEvents(term: string): void {
    const filteredGroupedEvents = this.newGroupedEvents.map(
      ([groupKey, events]) => {
        const filteredEvents = events.filter(
          (event) =>
            (this.filterArea.selectedFiltersCount === 0 ||
              this.filterArea.filters.some(
                (filter) =>
                  filter.isSelected &&
                  event.EventSeverity.includes(filter.value)
              )) &&
            event.Name?.toLowerCase().includes(term)
        );
        return [groupKey, filteredEvents] as [
          string,
          ODataNotificationAdminCenterEvent[]
        ];
      }
    );

    this.groupedEventsVisibleFlags.forEach(([_, eventFlags]) => {
      eventFlags.forEach((eventFlag) => {
        eventFlag.Visible = filteredGroupedEvents.some(([_, events]) =>
          events.some((e) => e.Key === eventFlag.Key)
        );
      });
    });

    this.accordionItemsHide.forEach(([key, val]) => {
      val.next(!this.showAccordion(key));
    });

    this.canBeCollapsed = true;
    this.newGroupedEvents.forEach(([groupKey]) => {
      this.expandedSections[groupKey] = true;
    });
  }

  hideNoEventsIcon() {
    return (
      this.groupedEventsVisibleFlags &&
      this.groupedEventsVisibleFlags.some(([_, events]) => {
        return events.some((event) => event.Visible);
      })
    );
  }

  hideAccordionEventList(groupKey: string): BehaviorSubject<boolean> {
    return this.accordionItemsHide.find(([key]) => key === groupKey)[1];
  }

  showAccordion(groupKey: string) {
    return this.groupedEventsVisibleFlags.some(([key, events]) => {
      return key === groupKey && events.some((event) => event.Visible);
    });
  }

  showEvent(groupKey: string, eventKey: string) {
    return this.groupedEventsVisibleFlags.some(([key, events]) => {
      return (
        key === groupKey &&
        events.some((event) => event.Key === eventKey && event.Visible)
      );
    });
  }

  shouldEnableResetButton(): boolean {
    return this.newGroupedEvents.some(([_, events]) =>
      events.some((event) =>
        event.NotificationAdminCenterEventConfigurations.some(
          (config) => config.ConfigurableByAdmin && !config.Checked
        )
      )
    );
  }

  isPrimaryRecipientVisible(): boolean {
    return this.productKey !== ProductType.Migration;
  }

  searchInputObservable(): Observable<string> {
    return merge(
      fromEvent(this.searchEventsInput.nativeElement, 'keyup').pipe(
        filter(
          (event: { code: string; target: { value: string } }) =>
            event.code === 'Enter'
        ),
        map(
          (event: { code: string; target: { value: string } }) =>
            event.target.value
        )
      ),
      fromEvent(this.searchEventsBtn.nativeElement, 'click').pipe(
        map(() => this.searchInputValue)
      ),
      fromEvent(this.clearSearchBtn.nativeElement, 'click').pipe(
        map(() => {
          this.searchInputValue = '';
          return '';
        })
      )
    ).pipe(distinctUntilChanged());
  }

  showClearFiltersButton(): boolean {
    return this.filterArea.selectedFiltersCount > 0;
  }

  checkFilter(filter: FilterItem): void {
    filter.isSelected = !filter.isSelected;
    this.updateSelectedFiltersCount();
    this.filterGroupedEvents(
      this.getSearchTermValue(this.searchTermSubject$.getValue())
    );
  }

  clearAllFilters(): void {
    this.filterArea.filters.forEach((filter) => {
      filter.isSelected = false;
    });
    this.updateSelectedFiltersCount();
    this.filterGroupedEvents(
      this.getSearchTermValue(this.searchTermSubject$.getValue())
    );
  }

  private updateSelectedFiltersCount(): void {
    this.filterArea.selectedFiltersCount = this.filterArea.filters.filter(
      (filter) => filter.isSelected
    ).length;
  }

  private getSearchTermValue(value: string) {
    return value?.toLowerCase() ?? '';
  }

  private setSeverityFilter() {
    const severityText = this.translateService.instant(
      'sk-notifications-admin-center.preferences.general.events_table.headers.severity'
    );
    this.filterArea = {
      displayName: severityText,
      key: severityText,
      selectedFiltersCount: 0,
      filters: EventSeverityValues.map((value) => {
        return new FilterItem(value, value);
      }),
    };
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.updateInitialPreferencesOnSave.complete();
    this.resetPreferencesOnCancel.complete();
  }
}
